import { Slider, Text, View } from '@/components'
import { SliderProps } from '@codeleap/web'
import React, { useCallback, useEffect, useRef } from 'react'

type QuestionSliderProps = Partial<SliderProps> & {
  showScale?: boolean
  rightLabel?: string
  leftLabel?: string
}

const scale = Array(11).fill(null)
function Scale({ showScale, min }: { showScale?: boolean; min: number }) {
  if (!showScale) return null

  return (
    <View variants={['fullWidth', 'row', 'justifySpaceBetween', 'relative']}>
      {scale.map((_, i) => i < min ? null : (
        <Text text={`${i}`} variants={['p4', 'color:neutral9', 'marginLeft:1']} key={`slider-label-${i}`} />
      ),
      )}
    </View>
  )
}

export function QuestionSlider({
  showScale,
  leftLabel = 'Completely dissatisfied',
  rightLabel = 'Completely satisfied',
  trackMarks,
  defaultValue,
  value: valueProp,
  min,
  onValueChange,
  ...rest
}: QuestionSliderProps) {
  const [value, setValue] = React.useState(valueProp ?? defaultValue)
  const valueRef = useRef(value?.[0])

  const changeTimer = useRef(null)

  const handleChange = useCallback(
    vl => {
      if (changeTimer.current) clearTimeout(changeTimer.current)

      setValue(vl)
      valueRef.current = vl?.[0]
      changeTimer.current = setTimeout(() => {
        onValueChange([valueRef.current])
      }, 200)
    },
    [onValueChange],
  )

  useEffect(() => {
    return () => {
      if (changeTimer.current) {
        clearTimeout(changeTimer.current)
        if (valueRef.current !== undefined) {
          onValueChange([valueRef.current])
        }
      }
    }
  }, [])

  return (
    <View variants={['column', 'fullWidth']}>
      <Scale showScale={showScale} min={min} />
      <Slider
        {...rest as any}
        debugName='QuestionSlider'
        value={value}
        onValueChange={handleChange}
        min={min}
        trackMarks={null}
        variants={['pulse', !showScale && 'pulse:noScale']}
        label={leftLabel}
        description={rightLabel}
        onPressThumbSetValue={false}
      />
    </View>
  )
}
